import { FC, useState, useCallback, useEffect, useRef } from 'react';
import { DangerousHTML, Modal, Typography, Image, Button, LinkAsButton, ButtonVariant, Accordion } from '@/shared/ui';
import { trackAccordionOpen, trackAccordionTimeSpent } from '@/loader/trackFaqAccordionOpen';
import { ROUTES_BY_SERVICE_CODE } from '@/router/config';
import { UserService } from '@/api/types';

interface ServiceDetailsModalProps {
  service: UserService;
  onClose: () => void;
}

export const InternalServicesDetailsModal: FC<ServiceDetailsModalProps> = ({ service, onClose }) => {
  const { name, icon, faq } = service;

  const [openTimes, setOpenTimes] = useState<Record<string, number>>({});
  const [activeKey, setActiveKey] = useState<string | undefined>(undefined);

  const activeKeyRef = useRef<string | undefined>(undefined);
  const openTimesRef = useRef<Record<string, number>>({});
  useEffect(() => {
    activeKeyRef.current = activeKey;
    openTimesRef.current = openTimes;
  }, [activeKey, openTimes]);

  const accordionItems = faq
    ? faq.map(([title, content], index) => ({
        key: title || `item-${index}`,
        label: <Typography variant="H4">{title}</Typography>,
        children: <DangerousHTML content={content} />,
      }))
    : [];

  const handleAccordionChange = useCallback(
    (newActiveKey: string | string[]) => {
      const currentKey = Array.isArray(newActiveKey) ? newActiveKey[0] : newActiveKey;

      if (activeKey && activeKey !== currentKey) {
        const start = openTimes[activeKey];
        if (start) {
          const duration = Math.round((Date.now() - start) / 1000);
          const title = faq?.find(([t]) => t === activeKey)?.[0] || activeKey;
          trackAccordionTimeSpent(name, title, duration);

          setOpenTimes((prev) => {
            const copy = { ...prev };
            delete copy[activeKey];
            return copy;
          });
        }
      }

      if (currentKey && currentKey !== activeKey) {
        const title = faq?.find(([t]) => t === currentKey)?.[0] || currentKey;
        setOpenTimes((prev) => ({ ...prev, [currentKey]: Date.now() }));
        trackAccordionOpen(name, title);
      }

      setActiveKey(currentKey);
    },
    [activeKey, openTimes, faq, name],
  );

  useEffect(() => {
    return () => {
      if (activeKeyRef.current) {
        const start = openTimesRef.current[activeKeyRef.current];
        if (start) {
          const duration = Math.round((Date.now() - start) / 1000);
          const title = faq?.find(([t]) => t === activeKeyRef.current)?.[0] || activeKeyRef.current;
          trackAccordionTimeSpent(name, title, duration);
        }
      }
    };
  }, [name, faq]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      if (activeKeyRef.current) {
        const start = openTimesRef.current[activeKeyRef.current];
        if (start) {
          const duration = Math.round((Date.now() - start) / 1000);
          const title = faq?.find(([t]) => t === activeKeyRef.current)?.[0] || activeKeyRef.current;
          trackAccordionTimeSpent(name, title, duration);
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [name, faq]);

  return (
    <Modal
      title={<Image src={icon} fallback="/images/icons-set.svg" alt={name} />}
      open={!!service}
      onCancel={onClose}
      classNames={{
        content: 'service_details',
        body: 'service_details__modal-body',
        footer: 'service_details__modal-footer',
        header: 'service_details__modal-header',
      }}
      footer={
        <>
          <LinkAsButton to={ROUTES_BY_SERVICE_CODE[service.code]} state={{ isFromService: true }}>
            {service.btn_cta || 'Замовити'}
          </LinkAsButton>

          <Button variant={ButtonVariant.SECONDARY} onClick={onClose}>
            Закрити
          </Button>
        </>
      }
    >
      <div className="service-details-modal__title">
        <Typography variant="H2">{name}</Typography>
      </div>
      <Accordion items={accordionItems} onChange={handleAccordionChange} />
    </Modal>
  );
};
